/**
 * Страница товара: слайдеры, галерея, раскрытие описания, панель "Добавить в проект".
 *
 * Plugin-зависимости (остаются на jQuery до миграции Slick→Swiper и Fancybox→PhotoSwipe):
 *   - #product_select_dim, #product_select_dim_mm — customSelect (jQuery-плагин)
 *   - #slider_product_01 / #slider_product_02 — Slick slider
 *   - .open-gallery — Fancybox gallery
 */

import { apiRequest } from '../../api.js';

const PRODUCT_SLIDER = '#product_slider';
const EXPAND_BLOCK = '#product_info_expand_block';
const EXPAND_BUTTON = '#product_info_expand_button';
const PROPS_BLOCK = '#product_props';
const PROJECT_LIST_BLOCK = '#product_add2project_list';
const PROJECT_LIST_BUTTON = '#product_add2project_button';
const PAGINATION_BUTTON = '#section_pagination_button';
const PAGINATION_CONTAINER = '#section_pagination_container';

// — UI handlers (native) —

function expandInfoText() {
    document.querySelector(EXPAND_BLOCK)?.classList.add('expanded');
    document.querySelector(PROPS_BLOCK)?.classList.add('no_bottom');
}

function selectImageSlide(trigger) {
    const imageSrc = trigger.getAttribute('data-src');
    if (!imageSrc) return;
    const pic = document.querySelector(`${PRODUCT_SLIDER} .product_slider_pic`);
    if (pic) pic.style.backgroundImage = `url('${imageSrc}')`;
}

function initPagination() {
    const totalPages = parseInt(document.querySelector('input[name=qty_pages]')?.value || '0', 10);
    let pageCounter = 1;

    document.addEventListener('click', e => {
        if (!e.target.closest(PAGINATION_BUTTON)) return;
        pageCounter++;
        if (pageCounter >= totalPages) {
            const btn = document.querySelector(PAGINATION_BUTTON);
            if (btn?.parentElement) btn.parentElement.style.display = 'none';
        }
    });
}

/**
 * AJAX-пагинация блока "YOU MAY ALSO LIKE".
 *
 * Список товаров пагинируется сервером (CollectionPaginator), поэтому
 * клик по номеру страницы должен догрузить нужную страницу и подменить
 * блок. Глобальный catalog-обработчик в core/main.js здесь не подходит:
 * он бьёт в `.js_products-list` (которого на странице товара нет) и
 * включает текущий товар в выборку — поэтому перехватываем клик первыми
 * через stopImmediatePropagation.
 */
function initAlsoLikePagination() {
    const section = document.querySelector('.product_detail_section');
    if (!section) return;

    const productId = document.querySelector('input[name=product_id]')?.value;
    if (!productId) return;

    document.addEventListener('click', async e => {
        const link = e.target.closest('.catalog-pagination-link[data-page]');
        if (!link || link.disabled) return;
        if (!section.contains(link)) return;

        // Не отдаём событие catalog-обработчику из core/main.js.
        e.stopImmediatePropagation();
        e.preventDefault();

        const targetPage = Number(link.dataset.page);
        if (!targetPage) return;

        // Держим ?page в URL — при перезагрузке сервер отрендерит ту же страницу.
        if (history.pushState) {
            const url = new URL(window.location.href);
            url.searchParams.set('page', targetPage);
            window.history.pushState({ path: url.href }, '', url.href);
        }

        const listEl = section.querySelector('.js_also-like-list');
        const paginationEl = section.querySelector('#catalog_pagination_container');
        if (!listEl) return;

        try {
            const response = await apiRequest(
                `/categories/product/${productId}/also-like?page=${targetPage}`,
            );
            if (typeof response?.html_products === 'string') {
                listEl.innerHTML = response.html_products;
            }
            if (paginationEl && typeof response?.html_pagination === 'string') {
                paginationEl.innerHTML = response.html_pagination;
            }
            document.querySelector(PAGINATION_CONTAINER)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        } catch (err) {
            console.error('also-like pagination failed:', err);
            window.showToast?.('error', 'Error loading products. Please try again.');
        }
    });
}

function initEventHandlers() {
    document.addEventListener('click', e => {
        // Image slide select
        const slide = e.target.closest(`${PRODUCT_SLIDER} [data-src]`);
        if (slide) {
            selectImageSlide(slide);
            return;
        }

        // Expand info text
        if (e.target.closest(EXPAND_BUTTON)) {
            expandInfoText();
            return;
        }

        // Toggle add-to-project panel
        if (e.target.closest(PROJECT_LIST_BUTTON)) {
            e.stopPropagation();
            document.querySelector(PROJECT_LIST_BLOCK)?.classList.toggle('active');
            return;
        }

        // Stop propagation when клик внутри панели — чтобы не закрывалась
        if (e.target.closest(PROJECT_LIST_BLOCK)) {
            e.stopPropagation();
            return;
        }

        // Клик вне — закрыть панель
        document.querySelector(PROJECT_LIST_BLOCK)?.classList.remove('active');
    });
}

// — customSelect / Slick / Fancybox (plugin bridges) —

function initCustomSelects() {
    if (typeof $ === 'undefined' || !$.fn.customSelect) return;
    const sel1 = $('#product_select_dim');
    const sel2 = $('#product_select_dim_mm');
    if (sel1.length) sel1.customSelect();
    if (sel2.length) sel2.customSelect();
}

// Публичное API (вызывается из inline Blade или других модулей)
window.initProductSliders = function (container) {
    if (typeof $ === 'undefined' || !$.fn.slick) return;

    const $ctx = $(container || document);

    const PARAMS_VERTICAL = {
        dots: false, infinite: false, speed: 500,
        slidesToShow: 4, slidesToScroll: 1,
        vertical: true, verticalSwiping: true,
        prevArrow: '', nextArrow: '',
    };

    const PARAMS_HORIZONTAL = {
        dots: false, infinite: false, speed: 500,
        slidesToShow: 5, slidesToScroll: 1,
        prevArrow: '', nextArrow: '',
        variableWidth: true, centerMode: false, adaptiveHeight: true,
    };

    const SLIDER_01 = $ctx.find('#slider_product_01');
    const SLIDER_02 = $ctx.find('#slider_product_02');

    if (SLIDER_01.hasClass('slick-initialized')) SLIDER_01.slick('unslick');
    if (SLIDER_02.hasClass('slick-initialized')) SLIDER_02.slick('unslick');

    // Показ стрелок по позиции: «назад» прячем в начале, «вперёд» — в конце,
    // обе прячем, когда листать нечего.
    const refreshArrows = ($slider, $prev, $next, visible) => {
        const total = $slider.find('.product_slider_list_item').length - 1; // без placeholder-элемента
        if (total <= visible) {
            $prev.addClass('d-none');
            $next.addClass('d-none');
            return;
        }
        const current = $slider.slick('slickCurrentSlide');
        $prev.toggleClass('d-none', current <= 0);
        $next.toggleClass('d-none', current >= total - visible);
    };

    if (SLIDER_01.length) {
        SLIDER_01.slick(PARAMS_VERTICAL);
        const $prev = $ctx.find('#slider_product_01_nav_prev_vertical');
        const $next = $ctx.find('#slider_product_01_nav_next_vertical');
        SLIDER_01.on('afterChange', () => refreshArrows(SLIDER_01, $prev, $next, PARAMS_VERTICAL.slidesToShow));
        refreshArrows(SLIDER_01, $prev, $next, PARAMS_VERTICAL.slidesToShow);
    }

    if (SLIDER_02.length) {
        SLIDER_02.slick(PARAMS_HORIZONTAL);
        const $prev = $ctx.find('#slider_product_02_nav_prev_horisontal');
        const $next = $ctx.find('#slider_product_02_nav_next_horisontal');
        SLIDER_02.on('afterChange', () => refreshArrows(SLIDER_02, $prev, $next, PARAMS_HORIZONTAL.slidesToShow));
        refreshArrows(SLIDER_02, $prev, $next, PARAMS_HORIZONTAL.slidesToShow);
    }

    // Nav prev/next (делегированно, один раз)
    if (!window._productSliderNavBound) {
        $(document).on('click', '#slider_product_01_nav_next_vertical', () => SLIDER_01.slick('slickNext'));
        $(document).on('click', '#slider_product_01_nav_prev_vertical', () => SLIDER_01.slick('slickPrev'));
        $(document).on('click', '#slider_product_02_nav_next_horisontal', () => SLIDER_02.slick('slickNext'));
        $(document).on('click', '#slider_product_02_nav_prev_horisontal', () => SLIDER_02.slick('slickPrev'));
        window._productSliderNavBound = true;
    }
};

window.initProductGallery = function (container, images) {
    if (typeof $ === 'undefined' || !$.fancybox) return;

    const ctx = container || document;
    const gallery = [];

    if (images && images.length) {
        images.forEach(src => gallery.push({ src }));
    } else {
        (ctx.querySelectorAll ? ctx : document).querySelectorAll('.product_slider_list_item[data-src]').forEach(el => {
            const src = el.getAttribute('data-src');
            if (src) gallery.push({ src });
        });
    }

    const trigger = (ctx.querySelector ? ctx : document).querySelector('.open-gallery');
    if (!trigger) return;

    // Очищаем прошлый handler jQuery, чтобы не было дублей
    $(trigger).off('click.productGallery').on('click.productGallery', () => {
        if (gallery.length > 0) {
            $.fancybox.open(gallery, { loop: false, buttons: ['slideShow', 'thumbs', 'close'] });
        }
    });
};

function init() {
    initEventHandlers();
    initPagination();
    initAlsoLikePagination();
    initCustomSelects();

    // Auto-init sliders и gallery, если элементы на странице
    if (document.querySelector('#slider_product_01') || document.querySelector('#slider_product_02')) {
        window.initProductSliders();
    }
    if (document.querySelector('.open-gallery')) {
        window.initProductGallery();
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
